import call from "./call";
import {
  QuestionItem,
  QuestionListDetailsResponse,
  QuestionListFull,
  QuestionListPartialUpdate,
  QuestionListResponse,
} from "../Models/question";

export const fetchQuestionLists = async (
  page: number = 1,
  page_size: number = 1000,
  search: string = "",
  cert_category: number | undefined,
  all: boolean = false,
  sort_order: string = "name",
  sort_direction: "asc" | "desc" = "asc",
): Promise<QuestionListResponse> => {
  const { data } = await call.get<QuestionListResponse>("/v1/question_list/", {
    params: {
      page,
      page_size,
      search,
      cert_category,
      all,
      sort_order,
      sort_direction,
    },
    snack: {
      423: "questions.loadLockedError",
      error: "questions.loadError",
    },
  });
  return data;
};

export const fetchQuestionListsForEvaluation = async (
  page: number = 1,
  page_size: number = 1000,
  search: string = "",
  cert_category?: number,
  all: boolean = true,
  archived: boolean = false,
): Promise<QuestionListResponse> => {
  const { data } = await call.get<QuestionListResponse>("/v1/question_list/", {
    params: { page, page_size, search, cert_category, all, archived },
  });
  return data;
};

export const setDefaultQuestionList = async (
  questionListId: number,
  categoryId: number,
): Promise<{
  category_id: number;
  message: string;
  question_list_id: number;
}> => {
  const response = await call.post(
    `/v1/question_list/default/${questionListId}/`,
    {
      category_id: categoryId,
    },
    {
      snack: {
        error: "questions.defaultSetError",
        success: "questions.defaultSetSuccess",
      },
    },
  );
  return response.data;
};

export const fetchUserQuestionLists = async (
  userId: number,
): Promise<QuestionListResponse> => {
  const { data } = await call.get<QuestionListResponse>(
    `/v1/question_list/user/${userId}/`,
  );
  return data;
};

export const fetchQuestionListDetails = async (
  id: number,
): Promise<QuestionListDetailsResponse> => {
  const response = await call.get(`/v1/question_list/${id}/`, {
    snack: {
      error: "questions.fetchForEvaluationError",
    },
  });
  return response.data;
};

export const fetchDefaultQuestionListDetails = async (
  id: number,
): Promise<QuestionItem> => {
  const response = await call.get(`/v1/question_list/certification/${id}/`, {
    snack: {
      error: "questions.fetchDefaultError",
    },
  });
  return response.data;
};

export const deleteQuestionList = async (id: number): Promise<void> => {
  await call.delete(`/v1/question_list/${id}/`, {
    snack: {
      success: "questions.deleteSuccess",
      error: "questions.deleteError",
    },
  });
};

export const toggleArchiveStatus = async (id: number): Promise<void> => {
  await call.put(`/v1/question_list/archive/${id}/`, {
    snack: {
      success: "questions.archiveToggleSuccess",
      error: "questions.archiveToggleError",
    },
  });
};

export const createQuestionListFull = async (
  questionListFull: QuestionListFull,
): Promise<QuestionListFull> => {
  const response = await call.post("/v1/question_list/", questionListFull, {
    snack: {
      error: "questions.createFetchError",
    },
  });
  return response.data;
};

export const updateQuestionListFull = async (
  questionListId: number,
  questionListFull: QuestionListFull,
) => {
  const response = await call.put(
    `/v1/question_list/${questionListId}/`,
    questionListFull,
  );
  return response;
};

export const updateQuestionListPartial = async (
  questionListId: number,
  data: QuestionListPartialUpdate,
) => {
  const response = await call.put(`/v2/question_list/${questionListId}/`, data);
  return response;
};

export const copyQuestionList = async (id: number) => {
  const response = await call.post(`/v1/question_list/copy/${id}/`, {
    snack: {
      success: "questions.copySuccess",
      error: "questions.copyError",
    },
  });
  return response.data;
};

export const exportQuestionList = async (
  id: number,
  format: "json" | "docx",
) => {
  const response = await call.get(
    `/v1/question_list/${id}/export/?format=${format}`,
    {
      responseType: "blob",
      snack: {
        error: "questions.exportError",
        success: "questions.exportSuccess",
      },
    },
  );
  return response;
};

export const shareQuestionList = async (id: number) => {
  const response = await call.post(`/v1/question_list/${id}/workspace/`, {
    snack: {
      success: "questions.shareSuccess",
      error: "questions.shareError",
    },
  });
  return response;
};

export const importQuestionList = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await call.post("/v1/question_list/import/", formData, {
    snack: {
      success: "questions.importSuccess",
      error: "questions.importError",
    },
  });

  return response.data;
};
